import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db,storage } from "../../../firebase";
import { containsBadWords } from "../../../utils/contentFilter";
import * as nsfwjs from "nsfwjs";
import Swal from "sweetalert2";

const CreatePostForm = ({ promoProfileId,userId, onCreated }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [objetivo, setObjetivo] = useState("");
  const [categoria, setCategoria] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [ctaTexto, setCtaTexto] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [descuento, setDescuento] = useState("");
  const [destacado, setDestacado] = useState(false);
  const [visibilidad, setVisibilidad] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [loadingNSFW, setLoadingNSFW] = useState(false);
  const modelRef = useRef(null);

  const [postsUsed, setPostsUsed] = useState(0);
  const [postsLimit, setPostsLimit] = useState(0);

  const maxMedia = 5;
  const fileInputRef = useRef(null);

  const maxImageTotalMB = 20;  // Tamaño total máximo de todas las imágenes en MB
  const maxVideoTotalMB = 30;  // Tamaño total máximo de todos los videos en MB
  const maxTotalMB = 50;       // Tamaño total combinado (imágenes + videos)


  // 🔹 Cargar modelo NSFW
  useEffect(() => {
    const loadModel = async () => {
      modelRef.current = await nsfwjs.load();
    };
    loadModel();
  }, []);

  // 🔹 Obtener límite de posts y los ya usados
  useEffect(() => {
    const fetchPostLimits = async () => {
      if (!promoProfileId || !userId) return;

      try {
        // Traer info del perfil
        const profileDoc = await getDoc(doc(db, `users/${userId}/promoProfiles/${promoProfileId}`));
        if (!profileDoc.exists()) return;

        const profileData = profileDoc.data();
        setPostsLimit(profileData.postsPerMonth || 0);

        // Contar publicaciones activas del perfil
        const postsSnap = await getDocs(
          query(collection(db, "PromotionalPosts"), where("promoProfileId", "==", promoProfileId))
        );
        setPostsUsed(postsSnap.docs.length);
      } catch (err) {
        console.error("Error obteniendo límite de publicaciones:", err);
      }
    };

    fetchPostLimits();
  }, [promoProfileId, userId]);


  const isFileNSFW = async (file) => {
    if (!modelRef.current) return false;

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const url = reader.result;
        const mediaElement = document.createElement(file.type.startsWith("video") ? "video" : "img");
        mediaElement.src = url;
        mediaElement.onloadeddata = async () => {
          const predictions = await modelRef.current.classify(mediaElement);
          const nsfw = predictions.find(p =>
            ['Porn', 'Hentai', 'Sexy'].includes(p.className) && p.probability > 0.7
          );
          resolve(!!nsfw);
        };
      };
      reader.readAsDataURL(file);
    });
  };

  
const uploadFile = async (file) => {
  const maxVideoSizeMB = 50;
  const isVideo = file.type.startsWith("video/");

  if (isVideo && file.size > maxVideoSizeMB * 1024 * 1024) {
    setError(`Los videos no pueden superar los ${maxVideoSizeMB}MB.`);
    return null;
  }

  try {
    setUploading(true);

    // Crear ruta única en Firebase Storage dentro de "PromotionalPosts"
    const filePath = `PromotionalPosts/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (err) {
    console.error("Error subiendo archivo a Firebase:", err);
    setError("Error subiendo archivo");
    return null;
  } finally {
    setUploading(false);
  }
};

  const getTotalSize = (type) => {
    return media
      .filter((m) => m.type === type)
      .reduce((acc, m) => acc + (m.fileSize || 0), 0); // Guardaremos fileSize en media
  };


const handleFileChange = async (e) => {
  const files = e.target.files;
  if (!files) return;

  if (media.length + files.length > maxMedia) {
    setError(`Solo puedes subir hasta ${maxMedia} archivos.`);
    return;
  }

  setError(null);

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    const newFileSizeMB = file.size / (1024 * 1024);
    const totalImageSizeMB = file.type.startsWith("image/")
      ? getTotalSize("image") / (1024 * 1024)
      : getTotalSize("image") / (1024 * 1024);
    const totalVideoSizeMB = file.type.startsWith("video/")
      ? getTotalSize("video") / (1024 * 1024)
      : getTotalSize("video") / (1024 * 1024);
    const totalCombinedMB = totalImageSizeMB + totalVideoSizeMB + newFileSizeMB;

    // Validaciones de tamaño
    if (file.type.startsWith("image/") && totalImageSizeMB + newFileSizeMB > maxImageTotalMB) {
      setError(`El tamaño total de las imágenes no puede superar ${maxImageTotalMB}MB.`);
      continue;
    }

    if (file.type.startsWith("video/") && totalVideoSizeMB + newFileSizeMB > maxVideoTotalMB) {
      setError(`El tamaño total de los videos no puede superar ${maxVideoTotalMB}MB.`);
      continue;
    }

    if (totalCombinedMB > maxTotalMB) {
      setError(`El tamaño total combinado de imágenes y videos no puede superar ${maxTotalMB}MB.`);
      continue;
    }

    // Analizar NSFW
    setLoadingNSFW(true);
    const nsfw = await isFileNSFW(file);
    setLoadingNSFW(false);

    if (nsfw) {
      Swal.fire(
        "Contenido bloqueado",
        "Este archivo contiene contenido inapropiado (NSFW).",
        "error"
      );
      continue;
    }

    // Subir archivo
    const url = await uploadFile(file);
    if (url) {
      setMedia((prev) => [
        ...prev,
        {
          url,
          type: file.type.startsWith("video/") ? "video" : "image",
          fileSize: file.size, // <-- Guardamos tamaño en bytes
        },
      ]);
    }
  }
};


  const removeMedia = (url) => {
    setMedia(media.filter((item) => item.url !== url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (postsUsed >= postsLimit) {
      setError(`Has alcanzado el máximo de ${postsLimit} publicaciones permitidas.`);
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle || containsBadWords(trimmedTitle)) {
      setError("Título inválido o contiene palabras inapropiadas.");
      return;
    }

    if (!trimmedDescription || containsBadWords(trimmedDescription)) {
      setError("Descripción inválida o contiene palabras inapropiadas.");
      return;
    }

    if (media.length === 0) {
      setError("Debes subir al menos una imagen o video");
      return;
    }

    setError(null);

    try {
      await addDoc(collection(db, "PromotionalPosts"), {
        promoProfileId,
        title: trimmedTitle,
        description: trimmedDescription,
        images: media.filter((m) => m.type === "image").map((m) => m.url),
        videos: media.filter((m) => m.type === "video").map((m) => m.url),
        estado: "activo",
        objetivo,
        categoria,
        fechaInicio,
        fechaFin,
        ctaTexto,
        ctaLink,
        descuento,
        destacado,
        visibilidad,
        createdAt: serverTimestamp(),
      });

      setPostsUsed((prev) => prev + 1); // 🔹 Incrementar contador al crear
      setTitle("");
      setDescription("");
      setMedia([]);
      setObjetivo("");
      setCategoria("");
      setFechaInicio("");
      setFechaFin("");
      setCtaTexto("");
      setCtaLink("");
      setDescuento("");
      setDestacado(false);
      setVisibilidad(true);

      if (onCreated) onCreated();
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Error creando publicación:", err);
      setError("Error al crear la publicación");
    }
  };

  const progressPercentage = postsLimit > 0 ? Math.min((postsUsed / postsLimit) * 100, 100) : 0;

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-900 rounded-lg shadow-lg space-y-6">
      
      {/* Barra de progreso de publicaciones */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 h-4 rounded-full mb-4">
        <div
          className="bg-green-500 h-4 rounded-full transition-all"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 text-center mb-2">
        {postsUsed} / {postsLimit} publicaciones usadas
      </p>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 text-center">
        Crear Nueva Publicación Promocional
      </h2>

      {/* Título y Descripción */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="title"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Título *
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            required
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Título de la publicación"
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Descripción
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Describe tu promoción, producto o evento"
          />
        </div>
      </div>

      {/* Upload Files */}
      <div>
        <label className="block mb-2 text-gray-700 dark:text-gray-300 font-semibold">
          Subir imágenes/videos (máx {maxMedia})
        </label>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || media.length >= maxMedia}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-white transition 
            ${
              uploading || media.length >= maxMedia
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800"
            } focus:outline-none focus:ring-4 focus:ring-blue-400 dark:focus:ring-blue-700`}
          aria-label="Seleccionar archivos multimedia"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-6 w-6 ${uploading ? "animate-spin" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v16h16"
            />
          </svg>
          {uploading ? "Subiendo..." : "Seleccionar archivos"}
        </button>
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileChange}
          disabled={uploading || media.length >= maxMedia}
          ref={fileInputRef}
          className="hidden"
        />
      </div>

      {/* Previsualización multimedia */}
      <div className="flex flex-wrap gap-4 mt-4">
        {media.map(({ url, type }) => (
          <div
            key={url}
            className="relative w-28 h-28 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700 shadow-lg group"
          >
            {type === "image" ? (
              <img
                src={url}
                alt="Preview"
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <video
                src={url}
                controls
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
            )}
            <button
              type="button"
              onClick={() => removeMedia(url)}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg hover:bg-red-700"
              aria-label="Eliminar archivo"
              title="Eliminar"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Otros campos: objetivo, categoria, fechas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label
            htmlFor="objetivo"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Objetivo
          </label>
          <select
            id="objetivo"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          >
            <option value="">Selecciona un objetivo</option>
            <option>Generar ventas</option>
            <option>Promocionar producto</option>
            <option>Reforzar marca</option>
            <option>Conseguir seguidores</option>
            <option>Promocionar evento</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="categoria"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Categoría
          </label>
          <select
            id="categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          >
            <option value="">Selecciona una categoría</option>
            <option>Producto</option>
            <option>Evento</option>
            <option>Servicio</option>
            <option>Promoción especial</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[150px]">
            <label
              htmlFor="fechaInicio"
              className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
            >
              Fecha de inicio
            </label>
            <input
              id="fechaInicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>

          <div className="flex-1">
            <label
              htmlFor="fechaFin"
              className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
            >
              Fecha de fin
            </label>
            <input
              id="fechaFin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>
          </div>
      </div>

      {/* CTA y descuento */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label
            htmlFor="ctaTexto"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Texto del botón (CTA)
          </label>
          <input
            id="ctaTexto"
            type="text"
            value={ctaTexto}
            onChange={(e) => setCtaTexto(e.target.value)}
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Ej: Comprar ahora"
          />
        </div>

        <div>
          <label
            htmlFor="ctaLink"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Enlace de acción (CTA)
          </label>
          <input
            id="ctaLink"
            type="url"
            value={ctaLink}
            onChange={(e) => setCtaLink(e.target.value)}
            placeholder="https://..."
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>

        <div>
          <label
            htmlFor="descuento"
            className="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
          >
            Descuento / Oferta
          </label>
          <input
            id="descuento"
            type="text"
            value={descuento}
            onChange={(e) => setDescuento(e.target.value)}
            placeholder="Ej: 20% OFF, 2x1, Envío gratis"
            className="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>
      </div>

      {/* Checkboxes destacados y visibilidad */}
      <div className="flex flex-wrap gap-8 items-center">
        <label className="inline-flex items-center space-x-2 cursor-pointer text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={destacado}
            onChange={(e) => setDestacado(e.target.checked)}
            className="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-500 rounded focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-700 transition"
          />
          <span className="select-none font-medium">¿Destacado?</span>
        </label>

        <label className="inline-flex items-center space-x-2 cursor-pointer text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={visibilidad}
            onChange={(e) => setVisibilidad(e.target.checked)}
            className="form-checkbox h-5 w-5 text-green-600 dark:text-green-500 rounded focus:ring-2 focus:ring-green-400 dark:focus:ring-green-700 transition"
          />
          <span className="select-none font-medium">Visible al público</span>
        </label>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-red-600 font-semibold animate-pulse text-center">{error}</p>
      )}

      {/* Submit */}
      <div className="text-center">
        <button
        type="submit"
        disabled={uploading || postsUsed >= postsLimit}
        className="inline-block w-full md:w-auto px-8 py-3 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-400 dark:focus:ring-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {uploading ? "Subiendo..." : postsUsed >= postsLimit ? "Máximo alcanzado" : "Crear Publicación"}
      </button>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center animate-fade-in">
            <h3 className="text-xl font-bold text-green-600 mb-4">¡Publicación creada!</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tu publicación promocional fue creada correctamente.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg transition"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default CreatePostForm;
